// src/pages/PartnerProgramPage.jsx
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HiOutlineUserGroup,
  HiOutlineStatusOnline,
  HiOutlineCurrencyDollar,
  HiOutlineTrendingUp,
  HiOutlineClipboardCopy,
  HiOutlineRefresh,
  HiOutlineLink,
  HiOutlineCash,
  HiOutlineFilter,
  HiOutlineXCircle
} from 'react-icons/hi';
import Modal from '../components/shared/Modal';
import {
  createPartnerInviteLink,
  createPartnerWithdrawal,
  getPartnerDashboard,
  getPartnerInviteLinks,
  getPartnerTransactions,
  getPartnerWithdrawals
} from '../api/apiClient';
import {
  buildReferralLink,
  generateReferralCode,
  normalizeReferralCode
} from '../utils/referral';

const accentStyles = {
  blue: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  emerald: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  amber: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
  rose: 'bg-rose-500/15 text-rose-400 border-rose-500/20',
  cyan: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/20',
  violet: 'bg-violet-500/15 text-violet-400 border-violet-500/20'
};

const statusStyles = {
  active: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  inactive: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
  new: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  paused: 'bg-amber-500/10 text-amber-400 border-amber-500/20'
};

const StatCard = ({ icon: Icon, label, value, accent = 'blue', loading }) => (
  <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5 shadow-lg">
    <div className="flex items-center justify-between">
      <div className={`p-2 rounded-lg border ${accentStyles[accent] || accentStyles.blue}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
    <div className="mt-4">
      {loading ? (
        <div className="h-8 w-24 rounded-lg bg-slate-700/70 animate-pulse" />
      ) : (
        <div className="text-2xl font-bold text-white">{value}</div>
      )}
      <div className="text-xs uppercase tracking-wider text-slate-400 mt-2">{label}</div>
    </div>
  </div>
);

const StatusPill = ({ status, label }) => (
  <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${statusStyles[status] || statusStyles.inactive}`}>
    {label}
  </span>
);

const TableTabButton = ({ active, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-2 rounded-lg border text-sm font-semibold transition-colors ${
      active
        ? 'bg-blue-500/20 text-blue-300 border-blue-500/40'
        : 'bg-slate-900/40 text-slate-300 border-slate-700/60 hover:bg-slate-800/60'
    }`}
  >
    {label}
  </button>
);

const PartnerProgramPage = () => {
  const { t, i18n } = useTranslation();
  const [isWithdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawNetwork, setWithdrawNetwork] = useState('BSC');
  const [referralCode, setReferralCode] = useState('');
  const [copyStatus, setCopyStatus] = useState('');
  const [inviteLinks, setInviteLinks] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [dashboardData, setDashboardData] = useState(null);
  const [withdrawalsData, setWithdrawalsData] = useState(null);
  const [transactionsData, setTransactionsData] = useState(null);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsError, setTransactionsError] = useState('');
  const [transactionFilters, setTransactionFilters] = useState({
    inviteCode: '',
    referredUserId: ''
  });
  const [activeTableTab, setActiveTableTab] = useState('referrals');
  const [withdrawForm, setWithdrawForm] = useState({ amount: '', address: '', comment: '' });
  const [withdrawStatus, setWithdrawStatus] = useState({ loading: false, error: '', success: '' });

  const withdrawNetworks = [
    { id: 'BSC', label: 'BSC', desc: 'BNB Smart Chain' },
    { id: 'POL', label: 'POL', desc: 'Polygon' },
    { id: 'SOL', label: 'SOL', desc: 'Solana' }
  ];

  const availableAmount = Number(withdrawalsData?.availableToWithdrawUsdt || 0);
  const pendingAmount = Number(withdrawalsData?.pendingWithdrawalUsdt || 0);
  const networkFee = 0;
  const requestedWithdrawAmount = Number(withdrawForm.amount || 0);
  const receiveAmount = Math.max(requestedWithdrawAmount - networkFee, 0).toFixed(2);

  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';

  const formatNumber = (value, digits = 2) => {
    const num = Number(value || 0);
    return num.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits });
  };

  const formatPercent = (value) => {
    const num = Number(value || 0);
    const minDigits = Number.isInteger(num) ? 0 : 2;
    return num.toLocaleString(locale, { minimumFractionDigits: minDigits, maximumFractionDigits: 2 });
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return '—';
    return date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getUserStatus = (user) => {
    if (!user) return 'inactive';
    const last = user.lastActivityAt ? new Date(user.lastActivityAt).getTime() : 0;
    if (!last && Number(user.tradesCount || 0) === 0) return 'new';
    const diffDays = last ? (Date.now() - last) / (1000 * 60 * 60 * 24) : 999;
    if (diffDays <= 7) return 'active';
    if (Number(user.tradesCount || 0) === 0) return 'new';
    return 'inactive';
  };

  const getWithdrawalStatusMeta = (statusValue) => {
    const status = String(statusValue || 'processing').toLowerCase();
    if (status === 'completed' || status === 'approved' || status === 'paid') {
      return {
        key: 'completed',
        className: 'border-emerald-500/30 text-emerald-300 bg-emerald-500/10'
      };
    }
    if (status === 'rejected' || status === 'failed' || status === 'cancelled') {
      return {
        key: 'rejected',
        className: 'border-rose-500/30 text-rose-300 bg-rose-500/10'
      };
    }
    return {
      key: 'processing',
      className: 'border-amber-500/30 text-amber-300 bg-amber-500/10'
    };
  };

  const latestInvite = useMemo(() => {
    if (!inviteLinks.length) return null;
    return [...inviteLinks].sort((a, b) => {
      const aTimeRaw = new Date(a?.createdAt || 0).getTime();
      const bTimeRaw = new Date(b?.createdAt || 0).getTime();
      const aTime = Number.isFinite(aTimeRaw) ? aTimeRaw : 0;
      const bTime = Number.isFinite(bTimeRaw) ? bTimeRaw : 0;
      return bTime - aTime;
    })[0];
  }, [inviteLinks]);

  const referralLink = latestInvite?.inviteUrl || (referralCode ? buildReferralLink(referralCode) : '');

  const totalPaidAmount = useMemo(() => {
    const items = withdrawalsData?.items || [];
    return items.reduce((sum, item) => sum + Number(item.amountUsdt ?? item.amount ?? 0), 0);
  }, [withdrawalsData]);

  const lastPayoutDate = useMemo(() => {
    const items = withdrawalsData?.items || [];
    const dates = items
      .map((item) => item.createdAt || item.updatedAt || item.completedAt)
      .filter(Boolean)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    return dates.length ? formatDate(dates[0]) : '—';
  }, [withdrawalsData]);

  useEffect(() => {
    const loadPartnerData = async () => {
      try {
        setPageError('');
        setPageLoading(true);
        const [dashboardRes, linksRes, withdrawalsRes, transactionsRes] = await Promise.all([
          getPartnerDashboard(50, 0),
          getPartnerInviteLinks(),
          getPartnerWithdrawals(100, 0),
          getPartnerTransactions({ limit: 100, offset: 0 })
        ]);
        setDashboardData(dashboardRes);
        setInviteLinks(linksRes?.items || []);
        setWithdrawalsData(withdrawalsRes);
        setTransactionsData(transactionsRes);
        if (!referralCode && linksRes?.items?.length) {
          setReferralCode(linksRes.items[0].code || '');
        }
      } catch (error) {
        setPageError(error.message || t('partner.error'));
      } finally {
        setPageLoading(false);
      }
    };

    loadPartnerData();
  }, []);

  const stats = useMemo(() => {
    const summary = dashboardData?.summary || {};
    return [
      { label: t('partner.stats.total_referrals'), value: Number(summary.totalUsers || 0).toLocaleString(locale), icon: HiOutlineUserGroup, accent: 'blue' },
      { label: t('partner.stats.active_referrals'), value: Number(summary.activeUsers || 0).toLocaleString(locale), icon: HiOutlineStatusOnline, accent: 'emerald' },
      { label: t('partner.stats.total_commission'), value: `$${formatNumber(summary.totalEarnedUsdt || 0, 2)}`, icon: HiOutlineCurrencyDollar, accent: 'amber' },
      { label: t('partner.stats.commission_30d'), value: `$${formatNumber(summary.totalEarned30dUsdt || 0, 2)}`, icon: HiOutlineTrendingUp, accent: 'cyan' }
    ];
  }, [dashboardData, locale, t]);

  const summary = dashboardData?.summary || {};
  const feeSharePercent = Number(summary.feeSharePercent ?? 0);

  const referrals = dashboardData?.users || [];
  const transactionItems = transactionsData?.items || transactionsData?.transactions || [];
  const withdrawalItems = withdrawalsData?.items || [];
  const withdrawalStatusSummary = withdrawalItems.reduce(
    (acc, item) => {
      const statusKey = getWithdrawalStatusMeta(item.status).key;
      acc[statusKey] += 1;
      return acc;
    },
    { processing: 0, completed: 0, rejected: 0 }
  );

  const topReferrals = useMemo(() => {
    const sorted = [...referrals].sort(
      (a, b) => Number(b.earnedTotalUsdt || 0) - Number(a.earnedTotalUsdt || 0)
    );
    const maxValue = Math.max(...sorted.map((ref) => Number(ref.earnedTotalUsdt || 0)), 1);
    return sorted.slice(0, 4).map((ref) => ({
      name: ref.login || ref.email || '—',
      commission: `$${formatNumber(ref.earnedTotalUsdt || 0, 2)}`,
      share: Math.min(100, Math.max(8, (Number(ref.earnedTotalUsdt || 0) / maxValue) * 100))
    }));
  }, [referrals]);

  const loadTransactions = async (filters = transactionFilters) => {
    try {
      setTransactionsError('');
      setTransactionsLoading(true);

      const data = await getPartnerTransactions({
        limit: 100,
        offset: 0,
        inviteCode: normalizeReferralCode(filters.inviteCode) || undefined,
        referredUserId: normalizeReferralCode(filters.referredUserId) || undefined
      });

      setTransactionsData(data);
    } catch (error) {
      setTransactionsError(error.message || t('partner.transactions.error'));
    } finally {
      setTransactionsLoading(false);
    }
  };

  const handleGenerateLink = async () => {
    setInviteError('');
    const normalized = normalizeReferralCode(referralCode) || generateReferralCode();
    const candidate = normalized.toLowerCase();
    if (!/^[a-z0-9_-]{3,64}$/.test(candidate)) {
      setInviteError(t('partner.invite.invalid_code'));
      return;
    }

    try {
      setInviteLoading(true);
      const res = await createPartnerInviteLink(candidate);
      const created = res?.link || res;
      setInviteLinks((prev) => {
        const exists = prev.some((item) => item.id === created?.id || item.code === created?.code);
        return exists ? prev : [created, ...prev];
      });
      setReferralCode(created?.code || candidate);
    } catch (error) {
      setInviteError(error.message || t('partner.invite.create_error'));
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopyLink = async (linkToCopy) => {
    if (!linkToCopy) return;
    try {
      await navigator.clipboard.writeText(linkToCopy);
      setCopyStatus('success');
    } catch (error) {
      setCopyStatus('error');
    } finally {
      setTimeout(() => setCopyStatus(''), 2000);
    }
  };

  const handleWithdrawSubmit = async () => {
    setWithdrawStatus({ loading: true, error: '', success: '' });
    const amount = Number(withdrawForm.amount);
    if (!amount || amount <= 0) {
      setWithdrawStatus({ loading: false, error: t('partner.withdrawals.error_amount_positive'), success: '' });
      return;
    }
    if (amount < 10) {
      setWithdrawStatus({ loading: false, error: t('partner.withdrawals.error_amount_min'), success: '' });
      return;
    }
    if (amount > availableAmount) {
      setWithdrawStatus({ loading: false, error: t('partner.withdrawals.error_insufficient'), success: '' });
      return;
    }
    if (!withdrawNetwork) {
      setWithdrawStatus({ loading: false, error: t('partner.withdrawals.error_network'), success: '' });
      return;
    }
    if (!withdrawForm.address.trim()) {
      setWithdrawStatus({ loading: false, error: t('partner.withdrawals.error_address'), success: '' });
      return;
    }

    try {
      await createPartnerWithdrawal({
        amountUsdt: amount,
        network: withdrawNetwork,
        address: withdrawForm.address.trim(),
        comment: withdrawForm.comment.trim() || undefined
      });
      setWithdrawStatus({ loading: false, error: '', success: t('partner.withdrawals.success') });
      setWithdrawForm({ amount: '', address: '', comment: '' });
      const refreshed = await getPartnerWithdrawals(100, 0);
      setWithdrawalsData(refreshed);
    } catch (error) {
      setWithdrawStatus({ loading: false, error: error.message || t('partner.withdrawals.error_generic'), success: '' });
    }
  };

  return (
    <div className="space-y-8 pb-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-white tracking-tight">{t('partner.title')}</h1>
        </div>
      </div>

      {pageError && (
        <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
          {pageError}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
        {stats.map((card) => (
          <StatCard key={card.label} {...card} loading={pageLoading} />
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        <div className="xl:col-span-2 relative overflow-hidden rounded-2xl border border-blue-500/20 bg-gradient-to-br from-slate-800/80 via-slate-800/70 to-blue-950/40 p-7 shadow-[0_20px_45px_-24px_rgba(37,99,235,0.55)]">
          <div className="pointer-events-none absolute -top-24 -right-16 h-52 w-52 rounded-full bg-blue-500/20 blur-3xl" />
          <div className="relative">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-2xl font-semibold text-white">{t('partner.invite.title')}</h2>
                <p className="text-sm text-slate-300 mt-1">{t('partner.invite.desc')}</p>
              </div>
            </div>

            <div className="mt-5">
              <div className="relative overflow-hidden rounded-xl border border-blue-400/30 bg-gradient-to-br from-blue-500/15 via-slate-900/35 to-cyan-500/10 p-5 sm:p-6 shadow-inner shadow-blue-950/35">
                <div className="pointer-events-none absolute inset-y-0 right-3 hidden w-56 sm:block">
                  <div className="absolute right-2 top-2 h-24 w-24 rounded-full border border-blue-300/25 bg-blue-400/10" />
                  <div className="absolute left-5 top-10 h-16 w-16 rounded-full border border-cyan-300/30 bg-cyan-300/10" />
                  <div className="absolute bottom-3 right-8 h-20 w-20 rounded-full border border-emerald-300/25 bg-emerald-300/10" />
                  <div className="absolute right-14 top-16 flex h-9 w-9 items-center justify-center rounded-full border border-blue-200/35 bg-blue-300/10 text-xs font-bold text-blue-100/80">
                    $
                  </div>
                  <div className="absolute bottom-7 left-16 flex h-8 w-8 items-center justify-center rounded-full border border-cyan-200/35 bg-cyan-300/10 text-[10px] font-bold text-cyan-100/80">
                    $
                  </div>
                  <div className="absolute right-0 top-5 rounded-full border border-emerald-300/35 bg-emerald-400/15 px-2.5 py-1 text-[11px] font-semibold text-emerald-200 shadow-[0_0_20px_rgba(52,211,153,0.25)]">
                    +10$
                  </div>
                  <div className="absolute left-8 top-14 rounded-full border border-cyan-300/35 bg-cyan-400/15 px-2.5 py-1 text-[11px] font-semibold text-cyan-100 shadow-[0_0_20px_rgba(34,211,238,0.22)]">
                    +5$
                  </div>
                  <div className="absolute bottom-8 right-6 rounded-full border border-blue-300/35 bg-blue-400/15 px-2.5 py-1 text-[11px] font-semibold text-blue-100 shadow-[0_0_20px_rgba(96,165,250,0.2)]">
                    +12$
                  </div>
                </div>

                <div className="relative z-10 pr-4 sm:pr-56">
                  <div className="text-6xl sm:text-7xl leading-none tracking-tight font-black text-blue-50 drop-shadow-[0_8px_24px_rgba(56,189,248,0.35)]">
                    {formatPercent(feeSharePercent)}%
                  </div>
                  <div className="mt-2 text-sm sm:text-base text-blue-100/85">{t('partner.invite.level1_desc')}</div>
                </div>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-wider text-slate-400">
                  {t('partner.invite.code_label')}
                </label>
                <input
                  type="text"
                  value={referralCode}
                  onChange={(event) => setReferralCode(event.target.value)}
                  placeholder={t('partner.invite.code_placeholder')}
                  className="w-full bg-slate-950/55 border border-slate-700 text-slate-100 rounded-2xl px-4 py-4 focus:outline-none focus:border-blue-400/70 shadow-inner shadow-slate-950/50"
                />
                <p className="text-xs text-slate-400">{t('partner.invite.custom_hint')}</p>
                {inviteError && <p className="text-xs text-rose-300">{inviteError}</p>}
              </div>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">
                  <HiOutlineLink className="w-5 h-5" />
                </div>
                <input
                  type="text"
                  readOnly
                  value={referralLink || t('partner.invite.placeholder')}
                  className="w-full bg-slate-950/55 border border-slate-700 text-slate-200 rounded-2xl pl-12 pr-4 py-4 focus:outline-none shadow-inner shadow-slate-950/50"
                />
              </div>
              <p className="text-xs text-slate-400">
                {latestInvite
                  ? t('partner.invite.latest_created', { date: formatDate(latestInvite.createdAt) })
                  : t('partner.invite.latest_empty')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={handleGenerateLink}
                  disabled={inviteLoading}
                  className="flex items-center justify-center gap-2 px-5 py-3.5 bg-blue-600 text-white rounded-2xl font-semibold shadow-lg shadow-blue-900/35 hover:bg-blue-500 transition-colors disabled:opacity-70"
                >
                  <HiOutlineRefresh className={`w-5 h-5 ${inviteLoading ? 'animate-spin' : ''}`} />
                  {inviteLoading ? t('partner.invite.creating') : t('partner.invite.btn_generate')}
                </button>
                <button
                  type="button"
                  onClick={() => handleCopyLink(referralLink)}
                  disabled={!referralLink}
                  className={`flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl font-semibold shadow-lg shadow-slate-900/40 transition-colors ${
                    referralLink
                      ? 'bg-slate-700/80 text-slate-100 hover:bg-slate-600/90'
                      : 'bg-slate-800/50 text-slate-500 cursor-not-allowed'
                  }`}
                >
                  <HiOutlineClipboardCopy className="w-5 h-5" />
                  {copyStatus === 'success' ? t('partner.invite.copy_success') : t('partner.invite.btn_copy')}
                </button>
              </div>
              {copyStatus === 'error' && (
                <p className="text-xs text-rose-300">{t('partner.invite.copy_error')}</p>
              )}
            </div>
          </div>
        </div>

        <div className="xl:col-span-2 relative overflow-hidden rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-slate-800/80 via-slate-800/70 to-emerald-950/40 p-7 shadow-[0_20px_45px_-24px_rgba(16,185,129,0.45)]">
          <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="relative flex h-full flex-col">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold text-white">{t('partner.withdrawals.title')}</h2>
              <div className="p-2.5 rounded-xl border border-emerald-500/30 bg-emerald-500/15 text-emerald-300">
                <HiOutlineCash className="w-5 h-5" />
              </div>
            </div>
            <div className="mt-6">
              <div className="text-4xl font-black text-white">${formatNumber(availableAmount, 2)}</div>
              <div className="text-xs uppercase tracking-wider text-slate-300 mt-1">{t('partner.withdrawals.available')}</div>
            </div>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-xl border border-slate-700/60 bg-slate-900/45 p-3">
                <div className="text-[11px] uppercase tracking-wider text-slate-400">{t('partner.payouts.pending')}</div>
                <div className="mt-1 text-lg font-semibold text-amber-300">${formatNumber(pendingAmount, 2)}</div>
              </div>
              <div className="rounded-xl border border-slate-700/60 bg-slate-900/45 p-3">
                <div className="text-[11px] uppercase tracking-wider text-slate-400">{t('partner.withdrawals.total_paid')}</div>
                <div className="mt-1 text-lg font-semibold text-slate-100">${formatNumber(totalPaidAmount, 2)}</div>
              </div>
            </div>
            <div className="mt-4 rounded-xl border border-slate-700/60 bg-slate-900/45 p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">{t('partner.withdrawals.last_payout')}</span>
                <span className="text-slate-100 font-mono">{lastPayoutDate}</span>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <div className="min-h-[92px] rounded-xl border border-amber-500/25 bg-amber-500/10 px-3 py-3.5 text-center flex flex-col items-center justify-center">
                <div className="text-[11px] uppercase tracking-wider text-amber-200/80">{t('partner.withdrawals.status.processing')}</div>
                <div className="mt-1 text-xl leading-none font-bold text-amber-200">{withdrawalStatusSummary.processing}</div>
              </div>
              <div className="min-h-[92px] rounded-xl border border-emerald-500/25 bg-emerald-500/10 px-3 py-3.5 text-center flex flex-col items-center justify-center">
                <div className="text-[11px] uppercase tracking-wider text-emerald-200/80">{t('partner.withdrawals.status.completed')}</div>
                <div className="mt-1 text-xl leading-none font-bold text-emerald-200">{withdrawalStatusSummary.completed}</div>
              </div>
              <div className="min-h-[92px] rounded-xl border border-rose-500/25 bg-rose-500/10 px-3 py-3.5 text-center flex flex-col items-center justify-center">
                <div className="text-[11px] uppercase tracking-wider text-rose-200/80">{t('partner.withdrawals.status.rejected')}</div>
                <div className="mt-1 text-xl leading-none font-bold text-rose-200">{withdrawalStatusSummary.rejected}</div>
              </div>
            </div>
            <div className="mt-auto pt-6">
              <button
                type="button"
                onClick={() => {
                  setWithdrawStatus({ loading: false, error: '', success: '' });
                  setWithdrawForm({ amount: availableAmount ? String(availableAmount.toFixed(2)) : '', address: '', comment: '' });
                  setWithdrawOpen(true);
                }}
                className="w-full flex items-center justify-center px-4 py-3.5 bg-emerald-500 hover:bg-emerald-400 text-white rounded-2xl font-semibold shadow-lg shadow-emerald-900/30 transition-colors"
              >
                {t('partner.withdrawals.button')}
              </button>
            </div>
          </div>
        </div>

        <div className="xl:col-span-4 bg-slate-800/60 border border-slate-700/50 rounded-xl p-5 shadow-lg">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-semibold">{t('partner.top.title')}</h3>
            <span className="text-[10px] uppercase tracking-widest text-slate-500">{t('partner.top.subtitle')}</span>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {topReferrals.length > 0 ? (
              topReferrals.map((ref) => (
                <div key={ref.name} className="rounded-xl border border-slate-700/60 bg-slate-900/45 p-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white truncate pr-3">{ref.name}</span>
                    <span className="text-emerald-400 font-mono">{ref.commission}</span>
                  </div>
                  <div className="h-2 bg-slate-700 rounded-full mt-3 overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${ref.share}%` }} />
                  </div>
                </div>
              ))
            ) : (
              <div className="text-xs text-slate-500">{t('partner.top.empty')}</div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl shadow-lg overflow-hidden">
        <div className="p-5 border-b border-slate-700/50">
          <h2 className="text-xl font-semibold text-white">{t('partner.table.title')}</h2>
          <p className="text-xs text-slate-400 mt-1">{t('partner.table.subtitle')}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <TableTabButton
              active={activeTableTab === 'referrals'}
              label={t('partner.tabs.referrals')}
              onClick={() => setActiveTableTab('referrals')}
            />
            <TableTabButton
              active={activeTableTab === 'links'}
              label={t('partner.tabs.links')}
              onClick={() => setActiveTableTab('links')}
            />
            <TableTabButton
              active={activeTableTab === 'transactions'}
              label={t('partner.tabs.transactions')}
              onClick={() => setActiveTableTab('transactions')}
            />
            <TableTabButton
              active={activeTableTab === 'withdrawals'}
              label={t('partner.tabs.withdrawals')}
              onClick={() => setActiveTableTab('withdrawals')}
            />
          </div>
        </div>

        {activeTableTab === 'referrals' && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-900/70 text-slate-400 uppercase text-[10px] tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">{t('partner.table.col_referral')}</th>
                  <th className="px-4 py-3 text-left">{t('partner.table.col_joined')}</th>
                  <th className="px-4 py-3 text-left">{t('partner.table.col_status')}</th>
                  <th className="px-4 py-3 text-right">{t('partner.table.col_trades')}</th>
                  <th className="px-4 py-3 text-right">{t('partner.table.col_volume')}</th>
                  <th className="px-4 py-3 text-right">{t('partner.table.col_commission_total')}</th>
                  <th className="px-4 py-3 text-right">{t('partner.table.col_commission_30d')}</th>
                  <th className="px-4 py-3 text-right">{t('partner.table.col_last_active')}</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((ref) => {
                  const status = getUserStatus(ref);
                  return (
                    <tr key={ref.userId || ref.email} className="border-t border-slate-700/40 hover:bg-slate-900/40">
                      <td className="px-4 py-3">
                        <div className="text-white font-medium">{ref.login || ref.email || '—'}</div>
                        <div className="text-xs text-slate-500">{ref.email || '—'}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-300">{formatDate(ref.registeredAt)}</td>
                      <td className="px-4 py-3">
                        <StatusPill status={status} label={t(`partner.status.${status}`)} />
                      </td>
                      <td className="px-4 py-3 text-right text-slate-300">{Number(ref.tradesCount || 0)}</td>
                      <td className="px-4 py-3 text-right text-slate-300">${formatNumber(ref.tradingVolume || 0, 2)}</td>
                      <td className="px-4 py-3 text-right text-emerald-400 font-mono">${formatNumber(ref.earnedTotalUsdt || 0, 2)}</td>
                      <td className="px-4 py-3 text-right text-blue-400 font-mono">${formatNumber(ref.earned30dUsdt || 0, 2)}</td>
                      <td className="px-4 py-3 text-right text-slate-400">{formatDate(ref.lastActivityAt)}</td>
                    </tr>
                  );
                })}
                {referrals.length === 0 && !pageLoading && (
                  <tr>
                    <td colSpan="8" className="px-4 py-6 text-center text-slate-500">
                      {t('partner.table.empty')}
                    </td>
                  </tr>
                )}
                {pageLoading && (
                  <tr>
                    <td colSpan="8" className="px-4 py-6 text-center text-slate-500">
                      {t('partner.loading')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTableTab === 'links' && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-900/70 text-slate-400 uppercase text-[10px] tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">{t('partner.links_table.col_code')}</th>
                  <th className="px-4 py-3 text-left">{t('partner.links_table.col_url')}</th>
                  <th className="px-4 py-3 text-right">{t('partner.links_table.col_invited')}</th>
                  <th className="px-4 py-3 text-right">{t('partner.links_table.col_accrual')}</th>
                  <th className="px-4 py-3 text-left">{t('partner.links_table.col_status')}</th>
                  <th className="px-4 py-3 text-left">{t('partner.links_table.col_created')}</th>
                  <th className="px-4 py-3 text-right">{t('partner.links_table.col_action')}</th>
                </tr>
              </thead>
              <tbody>
                {inviteLinks.map((link) => {
                  const url = link.inviteUrl || buildReferralLink(link.code);
                  const isActive = link.isActive !== false;
                  return (
                    <tr key={link.id || link.code} className="border-t border-slate-700/40 hover:bg-slate-900/40">
                      <td className="px-4 py-3 text-white font-semibold">{link.code || '—'}</td>
                      <td className="px-4 py-3 text-slate-300 font-mono text-xs max-w-[420px] truncate">{url || '—'}</td>
                      <td className="px-4 py-3 text-right text-slate-300">{Number(link.invitedUsers || 0)}</td>
                      <td className="px-4 py-3 text-right text-emerald-400 font-mono">${formatNumber(link.totalAccrualUsdt || 0, 2)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] px-2 py-1 rounded-full border uppercase tracking-widest ${
                          isActive
                            ? 'border-emerald-500/30 text-emerald-300 bg-emerald-500/10'
                            : 'border-slate-600/40 text-slate-400 bg-slate-800/40'
                        }`}>
                          {isActive ? t('partner.invite.active') : t('partner.invite.inactive')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-400">{formatDate(link.createdAt)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => handleCopyLink(url)}
                          className="inline-flex items-center justify-center rounded-lg border border-slate-700/70 bg-slate-900/60 px-2.5 py-2 text-slate-200 hover:bg-slate-800 hover:text-white transition-colors"
                        >
                          <HiOutlineClipboardCopy className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {inviteLinks.length === 0 && !pageLoading && (
                  <tr>
                    <td colSpan="7" className="px-4 py-6 text-center text-slate-500">
                      {t('partner.invite.links_empty')}
                    </td>
                  </tr>
                )}
                {pageLoading && (
                  <tr>
                    <td colSpan="7" className="px-4 py-6 text-center text-slate-500">
                      {t('partner.loading')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTableTab === 'transactions' && (
          <div>
            <div className="p-4 border-b border-slate-700/50 bg-slate-900/30">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 items-end">
                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-wider text-slate-500">{t('partner.transactions.filter_invite_code')}</label>
                  <select
                    value={transactionFilters.inviteCode}
                    onChange={(event) => setTransactionFilters((prev) => ({ ...prev, inviteCode: event.target.value }))}
                    className="w-full rounded-xl border border-slate-700 bg-slate-900/60 px-3 py-2.5 text-sm text-slate-200 focus:outline-none focus:border-blue-500/50"
                  >
                    <option value="">{t('partner.transactions.filter_all_codes')}</option>
                    {inviteLinks.map((link) => (
                      <option key={link.id || link.code} value={link.code}>
                        {link.code}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1 lg:col-span-2">
                  <label className="text-xs uppercase tracking-wider text-slate-500">{t('partner.transactions.filter_referred_user_id')}</label>
                  <input
                    type="text"
                    value={transactionFilters.referredUserId}
                    onChange={(event) =>
                      setTransactionFilters((prev) => ({ ...prev, referredUserId: event.target.value }))
                    }
                    placeholder={t('partner.transactions.filter_referred_user_placeholder')}
                    className="w-full rounded-xl border border-slate-700 bg-slate-900/60 px-3 py-2.5 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-blue-500/50"
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => loadTransactions(transactionFilters)}
                    disabled={transactionsLoading}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl border border-blue-500/40 bg-blue-500/20 px-3 py-2.5 text-sm font-semibold text-blue-300 hover:bg-blue-500/30 transition-colors disabled:opacity-60"
                  >
                    <HiOutlineFilter className="w-4 h-4" />
                    {transactionsLoading ? t('partner.transactions.loading_action') : t('partner.transactions.apply')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const emptyFilters = { inviteCode: '', referredUserId: '' };
                      setTransactionFilters(emptyFilters);
                      loadTransactions(emptyFilters);
                    }}
                    className="inline-flex items-center justify-center rounded-xl border border-slate-700 bg-slate-900/60 px-3 py-2.5 text-slate-300 hover:bg-slate-800 transition-colors"
                  >
                    <HiOutlineXCircle className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {transactionsError && (
                <div className="mt-3 rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
                  {transactionsError}
                </div>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-900/70 text-slate-400 uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">{t('partner.transactions.col_date')}</th>
                    <th className="px-4 py-3 text-left">{t('partner.transactions.col_invite_code')}</th>
                    <th className="px-4 py-3 text-left">{t('partner.transactions.col_user')}</th>
                    <th className="px-4 py-3 text-left">{t('partner.transactions.col_status')}</th>
                    <th className="px-4 py-3 text-right">{t('partner.transactions.col_amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {transactionItems.map((item) => {
                    const txId = item.id || item.transactionId || item._id || `${item.createdAt || ''}-${item.inviteCode || ''}`;
                    const txDate = item.createdAt || item.accruedAt || item.updatedAt || item.timestamp;
                    const txCode = item.inviteCode || item.code || '—';
                    const txUser =
                      item.referredUserLogin ||
                      item.referredLogin ||
                      item.login ||
                      item.email ||
                      item.referredUserId ||
                      '—';
                    const txStatus = item.status || item.type || '—';
                    const txAmount = Number(item.amountUsdt ?? item.accrualUsdt ?? item.commissionUsdt ?? item.amount ?? 0);

                    return (
                      <tr key={txId} className="border-t border-slate-700/40 hover:bg-slate-900/40">
                        <td className="px-4 py-3 text-slate-300">{formatDate(txDate)}</td>
                        <td className="px-4 py-3 text-slate-300 font-mono">{txCode}</td>
                        <td className="px-4 py-3 text-white">{txUser}</td>
                        <td className="px-4 py-3 text-slate-300">{txStatus}</td>
                        <td className="px-4 py-3 text-right text-emerald-400 font-mono">${formatNumber(txAmount, 2)}</td>
                      </tr>
                    );
                  })}

                  {transactionItems.length === 0 && !transactionsLoading && (
                    <tr>
                      <td colSpan="5" className="px-4 py-6 text-center text-slate-500">
                        {t('partner.transactions.empty')}
                      </td>
                    </tr>
                  )}
                  {transactionsLoading && (
                    <tr>
                      <td colSpan="5" className="px-4 py-6 text-center text-slate-500">
                        {t('partner.transactions.loading')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTableTab === 'withdrawals' && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-900/70 text-slate-400 uppercase text-[10px] tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">{t('partner.withdrawals.table.col_date')}</th>
                  <th className="px-4 py-3 text-right">{t('partner.withdrawals.table.col_amount')}</th>
                  <th className="px-4 py-3 text-left">{t('partner.withdrawals.table.col_network')}</th>
                  <th className="px-4 py-3 text-left">{t('partner.withdrawals.table.col_address')}</th>
                  <th className="px-4 py-3 text-left">{t('partner.withdrawals.table.col_status')}</th>
                  <th className="px-4 py-3 text-left">{t('partner.withdrawals.table.col_comment')}</th>
                </tr>
              </thead>
              <tbody>
                {withdrawalItems.map((item) => {
                  const rowId =
                    item.id ||
                    item.withdrawalId ||
                    item._id ||
                    `${item.createdAt || ''}-${item.amountUsdt || item.amount || 0}`;
                  const rowDate = item.createdAt || item.requestedAt || item.updatedAt || item.timestamp;
                  const rowAmount = Number(item.amountUsdt ?? item.amount ?? 0);
                  const rowNetwork = item.network || item.chain || '—';
                  const rowAddress = item.payoutAddress || item.address || '—';
                  const rowComment = item.comment || item.txHash || item.transactionHash || '—';
                  const rowStatus = getWithdrawalStatusMeta(item.status);

                  return (
                    <tr key={rowId} className="border-t border-slate-700/40 hover:bg-slate-900/40">
                      <td className="px-4 py-3 text-slate-300">{formatDate(rowDate)}</td>
                      <td className="px-4 py-3 text-right text-emerald-400 font-mono">${formatNumber(rowAmount, 2)}</td>
                      <td className="px-4 py-3 text-slate-300">{rowNetwork}</td>
                      <td className="px-4 py-3 text-slate-300 font-mono text-xs max-w-[320px] truncate">{rowAddress}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] px-2 py-1 rounded-full border uppercase tracking-widest ${rowStatus.className}`}>
                          {t(`partner.withdrawals.status.${rowStatus.key}`)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-300 text-xs max-w-[280px] truncate">{rowComment}</td>
                    </tr>
                  );
                })}
                {withdrawalItems.length === 0 && !pageLoading && (
                  <tr>
                    <td colSpan="6" className="px-4 py-6 text-center text-slate-500">
                      {t('partner.withdrawals.table.empty')}
                    </td>
                  </tr>
                )}
                {pageLoading && (
                  <tr>
                    <td colSpan="6" className="px-4 py-6 text-center text-slate-500">
                      {t('partner.loading')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={isWithdrawOpen}
        onClose={() => setWithdrawOpen(false)}
        title={t('partner.withdrawals.modal_title')}
      >
        <div className="space-y-5">
          <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500">{t('partner.withdrawals.available')}</div>
            <div className="mt-2 text-2xl font-bold text-white">${formatNumber(availableAmount, 2)}</div>
          </div>

          <div className="space-y-2">
            <label className="text-sm text-slate-300">{t('partner.withdrawals.network')}</label>
            <div className="grid grid-cols-3 gap-2">
              {withdrawNetworks.map((network) => {
                const isActive = withdrawNetwork === network.id;
                return (
                  <button
                    key={network.id}
                    type="button"
                    onClick={() => setWithdrawNetwork(network.id)}
                    className={`rounded-xl border px-3 py-3 text-left transition-colors ${
                      isActive
                        ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300'
                        : 'border-slate-700 bg-slate-900/60 text-slate-300 hover:border-slate-500'
                    }`}
                  >
                    <div className="text-sm font-semibold">{network.label}</div>
                    <div className="text-[10px] text-slate-500 mt-1">{network.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm text-slate-300">{t('partner.withdrawals.address')}</label>
            <input
              type="text"
              placeholder={t('partner.withdrawals.address_placeholder')}
              value={withdrawForm.address}
              onChange={(event) => setWithdrawForm((prev) => ({ ...prev, address: event.target.value }))}
              className="w-full rounded-xl border border-slate-700 bg-slate-900/60 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500/50"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm text-slate-300">{t('partner.withdrawals.amount')}</label>
            <div className="relative">
              <input
                type="text"
                value={withdrawForm.amount}
                onChange={(event) => setWithdrawForm((prev) => ({ ...prev, amount: event.target.value }))}
                className="w-full rounded-xl border border-slate-700 bg-slate-900/60 px-4 py-3 text-white focus:outline-none"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs uppercase text-slate-500">USDT</span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm text-slate-300">{t('partner.withdrawals.comment')}</label>
            <input
              type="text"
              placeholder={t('partner.withdrawals.comment_placeholder')}
              value={withdrawForm.comment}
              onChange={(event) => setWithdrawForm((prev) => ({ ...prev, comment: event.target.value }))}
              className="w-full rounded-xl border border-slate-700 bg-slate-900/60 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500/50"
            />
          </div>

          <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-4 text-sm space-y-2">
            <div className="flex items-center justify-between text-slate-400">
              <span>{t('partner.withdrawals.fee')}</span>
              <span className="text-white font-mono">${formatNumber(networkFee, 2)}</span>
            </div>
            <div className="flex items-center justify-between text-slate-300">
              <span>{t('partner.withdrawals.receive')}</span>
              <span className="text-emerald-400 font-mono">${receiveAmount}</span>
            </div>
          </div>

          {withdrawStatus.error && (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
              {withdrawStatus.error}
            </div>
          )}
          {withdrawStatus.success && (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">
              {withdrawStatus.success}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => setWithdrawOpen(false)}
              className="flex-1 rounded-xl bg-slate-700/80 px-4 py-3 text-white font-semibold hover:bg-slate-600 transition-colors"
            >
              {t('partner.withdrawals.cancel')}
            </button>
            <button
              type="button"
              onClick={handleWithdrawSubmit}
              disabled={withdrawStatus.loading}
              className="flex-1 rounded-xl bg-emerald-500 px-4 py-3 text-white font-semibold hover:bg-emerald-400 transition-colors disabled:opacity-70"
            >
              {withdrawStatus.loading ? t('partner.withdrawals.processing') : t('partner.withdrawals.confirm')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PartnerProgramPage;
